import React, { useState, useEffect } from 'react';
import { Partido, getCategoriaLabel, getCompeticionLabel } from '@/types';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import CameraCapture from '@/components/ui/CameraCapture';
import { partidoService } from '@/services/PartidoService';
import { Check, X, ExternalLink, MapPin } from 'lucide-react';
import { fileToBase64WithoutPrefix, optimizeImageForUpload } from '@/utils/imageUpload';
import { openExternalUrl } from '@/utils/externalLink';
import { abrirEnMapas, hayUbicacion } from '@/utils/abrirEnMapas';

interface DetallePartidoProps {
  isOpen: boolean;
  onClose: () => void;
  partido: Partido | null;
  onVerInformes?: (partidoId: number) => void;
  onCrearInforme?: (partidoId: number) => void;
  onPartidoActualizado?: () => void;
}

/**
 * Modal para mostrar los detalles completos de un partido
 * @param isOpen - Si el modal está abierto
 * @param onClose - Función para cerrar el modal
 * @param partido - Datos del partido a mostrar
 * @param onVerInformes - Función para ver informes del partido
 * @param onCrearInforme - Función para crear un nuevo informe
 */
export const DetallePartido: React.FC<DetallePartidoProps> = ({
  isOpen,
  onClose,
  partido,
  onVerInformes,
  onCrearInforme,
  onPartidoActualizado // Nuevo prop
}) => {
  const [destacado, setDestacado] = useState<boolean>(false);
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [fotoPartido, setFotoPartido] = useState<string | null>(null);
  const [loadingFoto, setLoadingFoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  
  // Simplificar el useEffect:
  useEffect(() => {
      if (partido && isOpen) {
          // Inicializar destacado correctamente
          setDestacado(Boolean(partido.destacado));
          
          // Inicializar foto si existe
          if (partido.foto) {
              setFotoPreview(`data:image/jpeg;base64,${partido.foto}`);
          } else {
              setFotoPreview(null);
          }
          
          // Limpiar la foto anterior del partido
          setFotoPartido(null);
          
          limpiarMensajes();
          setFoto(null);
          setHasChanges(false);
          setUploadProgress(null);
      }
  }, [partido, isOpen]);

  // Cargar la foto cuando se abre el modal o cambia el partido
  useEffect(() => {
    if (isOpen && partido) {
      cargarFotoPartido();
    } else {
      // Si no hay foto, limpiar el estado
      setFotoPartido(null);
    }
  }, [isOpen, partido]);

  const cargarFotoPartido = async () => {
    if (!partido) return;
    
    try {
      setLoadingFoto(true);
      const response = await partidoService.obtenerFotoPartido(partido.id);
      if (response.success && response.data) {
        setFotoPartido(response.data);
      }
    } catch (error) {
      console.warn('No se pudo cargar la foto del partido:', error);
    } finally {
      setLoadingFoto(false);
    }
  };

  /**
   * Limpia los mensajes de estado
   */
  const limpiarMensajes = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  /**
   * Maneja la selección de una nueva foto
   */
  const handleFotoSelect = (file: File | null) => {
    setFoto(file);
    setUploadProgress(null);
    if (file) {
      const url = URL.createObjectURL(file);
      setFotoPreview(url);
    }
    setHasChanges(true);
    limpiarMensajes();
  };

  /**
   * Elimina la foto actual
   */
  const eliminarFoto = () => {
    setFoto(null);
    setFotoPreview(null);
    setUploadProgress(null);
    setHasChanges(true);
    limpiarMensajes();
  };

  /**
   * Detecta cambios en los campos
   */
  useEffect(() => {
    if (partido) {
      const valorOriginal = Boolean(partido.destacado);
      const destacadoChanged = destacado !== valorOriginal;      
      const fotoChanged = foto !== null || (fotoPreview === null && Boolean(partido.foto));
      setHasChanges(destacadoChanged || fotoChanged);
    }
  }, [destacado, foto, fotoPreview, partido]);

  if (!partido) return null;

  /**
   * Formatea la fecha y hora del partido en formato DD-MM-YYYY, HH:MM
   */
  const formatearFechaHora = (fechaHora: string): string => {
    const fecha = new Date(fechaHora);
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    const horas = fecha.getHours().toString().padStart(2, '0');
    const minutos = fecha.getMinutes().toString().padStart(2, '0');
    
    return `${dia}-${mes}-${año}, ${horas}:${minutos}`;
  };

  /**
   * Maneja el clic en ver informes
   */
  const handleVerInformes = () => {
    if (onVerInformes) {
      onVerInformes(partido.id);
    }
  };

  /**
   * Maneja el clic en ver partido (abrir URL externa)
   */
  const handleVerPartido = () => {
    if (partido?.urlActa) {
      openExternalUrl(partido.urlActa);
    }
  };

  /**
   * Maneja el clic en crear informe
   */
  const handleCrearInforme = () => {
    if (onCrearInforme) {
      onCrearInforme(partido.id);
    }
  };

  /**
   * Maneja el cambio del estado destacado
   */
  const handleDestacadoChange = (valor: boolean) => {
    setDestacado(valor);
  };

  /**
   * Maneja el guardado de la información adicional
   */
  const handleGuardar = async () => {
    if (!partido || !hasChanges) return;

    let guardadoConExito = false;
    try {
      setIsLoading(true);
      limpiarMensajes();
      setUploadProgress(null);

      let fotoBase64: string | null | undefined;
      
      // Si hay una nueva foto seleccionada, convertirla a base64
      if (foto) {
        setUploadProgress(0);
        const optimizedFile = await optimizeImageForUpload(foto);
        fotoBase64 = await fileToBase64WithoutPrefix(optimizedFile);
      } else if (!fotoPreview) {
        // Si no hay preview, significa que se eliminó la foto
        fotoBase64 = null;
      }
      // Si hay preview pero no hay foto nueva, no enviamos el campo foto (mantener la existente)

      const datosActualizacion: any = {};
      
      // Solo incluir campos que han cambiado
      if (destacado !== (partido.destacado || false)) {
        datosActualizacion.destacado = destacado;
      }
      
      if (fotoBase64 !== undefined) {
        datosActualizacion.fotoBase64 = fotoBase64;
      }

      await partidoService.actualizarInformacionAdicional(
        partido.id,
        datosActualizacion,
        fotoBase64 !== undefined ? (percentage) => setUploadProgress(percentage) : undefined
      );
      setUploadProgress(100);
      guardadoConExito = true;
      
      setSuccessMessage('Información guardada correctamente');
      setHasChanges(false);
      
      // Notificar al componente padre que se actualizó el partido
      if (onPartidoActualizado) {
        onPartidoActualizado();
      }
      
      // Cerrar el modal después de guardar exitosamente
      setTimeout(() => {
        onClose();
      }, 1000);
      
    } catch (error) {
      setUploadProgress(null);
      setErrorMessage('Error al guardar la información. Inténtalo de nuevo.');
    } finally {
      if (!guardadoConExito) setUploadProgress(null);
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="space-y-6">
        {/* Título del partido */}
        <div className="text-center border-b border-secondary-700 pb-4 relative">
          <button 
            onClick={onClose}
            className="absolute top-0 right-0 p-2 text-secondary-400 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold text-secondary-50 mb-2 px-8">
            {partido.nombre}
          </h2>
          <p className="text-secondary-400 text-sm">
            Detalles del partido
          </p>
        </div>

        {/* Mensajes de estado - Movidos aquí para mayor visibilidad */}
        {(errorMessage || successMessage) && (
          <div className="space-y-2">
            {errorMessage && (
              <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg text-sm">
                {errorMessage}
              </div>
            )}
            {successMessage && (
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-2 rounded-lg text-sm">
                {successMessage}
              </div>
            )}
          </div>
        )}

        {/* Información principal */}
        <div className="space-y-4">
          {/* Información del partido en grid de 2 columnas */}
          <div className="bg-secondary-800 rounded-lg p-4 border border-secondary-700">
            <h3 className="text-lg font-semibold text-secondary-50 mb-4">
              Información del Partido
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Fecha y Hora */}
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1 leading-tight">
                  Fecha y Hora
                </label>
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {formatearFechaHora(partido.fechaHora)}
                </div>
              </div>

              {/* Competición */}
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1 leading-tight">
                  Competición
                </label>
                <div className="bg-primary-500/20 text-primary-400 px-3 py-2 rounded-lg text-sm font-semibold">
                  {getCompeticionLabel(partido.competicion)}
                </div>
              </div>

              {/* Categoría */}
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1 leading-tight">
                  Categoría
                </label>
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  {getCategoriaLabel(partido.categoria)}
                </div>
              </div>

              {/* Grupo */}
              <div>
                <label className="block text-xs font-medium text-secondary-400 mb-1 leading-tight">
                  Grupo
                </label>
                <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                  Grupo {partido.grupo}
                </div>
              </div>
            </div>

            {/* Campo de juego: pulsable si tenemos coordenadas */}
            {partido.campo && (
              <div className="mt-4">
                <label className="block text-xs font-medium text-secondary-400 mb-1 leading-tight">
                  Campo
                </label>

                {hayUbicacion(partido.latitud, partido.longitud) ? (
                  <button
                    type="button"
                    onClick={() => abrirEnMapas(partido.latitud, partido.longitud, partido.campo)}
                    className="w-full flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/40 px-3 py-2.5 rounded-lg text-left transition-all active:bg-emerald-500/20 active:scale-[0.99]"
                  >
                    <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-secondary-200 leading-tight">{partido.campo}</p>
                      <p className="text-[11px] font-bold text-emerald-400 uppercase tracking-wide mt-0.5">
                        Cómo llegar
                      </p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  </button>
                ) : (
                  <div className="bg-secondary-700 text-secondary-300 px-3 py-2 rounded-lg text-sm">
                    {partido.campo}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Descripción */}
          {partido.descripcion && (
            <div className="bg-secondary-800 rounded-lg p-4 border border-secondary-700">
              <h3 className="text-lg font-semibold text-secondary-50 mb-3">
                Descripción
              </h3>
              <p className="text-secondary-300 leading-relaxed">
                {partido.descripcion}
              </p>
            </div>
          )}

          {/* Sección de foto */}
          <div className="bg-secondary-800 rounded-lg p-4 border border-secondary-700">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-secondary-50">Foto del Partido</h3>
              
              {loadingFoto && (
                <div className="text-secondary-400 text-center py-4">
                  Cargando foto...
                </div>
              )}
              
              {fotoPartido && !loadingFoto && (
                <div className="flex justify-center">
                  <img 
                    src={`data:image/jpeg;base64,${fotoPartido}`}
                    alt={`Foto del partido ${partido?.nombre}`}
                    className="max-w-full max-h-64 object-contain rounded-lg"
                  />
                </div>
              )}
              
              {!loadingFoto && !fotoPartido && (
                <div className="text-secondary-400 text-center py-4">
                  Este partido no tiene foto
                </div>
              )}
            </div>
          </div>

          {/* Información Adicional */}
          <div className="bg-secondary-800 rounded-lg p-4 border border-secondary-700">
            <h3 className="text-lg font-semibold text-secondary-50 mb-4">
              Información Adicional
            </h3>
            
            <div className="space-y-4">
              {/* Jugador Destacado */}
              <div>
                <label className="block text-sm font-medium text-secondary-300 mb-3">
                  ¿Hay algún jugador destacado?
                </label>
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleDestacadoChange(true)}
                    disabled={isLoading}
                    variant={destacado ? "primary" : "outline"}
                    className={`flex-1 flex items-center justify-center gap-2 ${
                      destacado 
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : 'bg-transparent text-secondary-400 border-secondary-600 hover:border-secondary-500'
                    }`}
                  >
                    <Check size={16} />
                    Sí
                  </Button>
                  
                  <Button
                    onClick={() => handleDestacadoChange(false)}
                    disabled={isLoading}
                    variant={!destacado ? "primary" : "outline"}
                    className={`flex-1 flex items-center justify-center gap-2 ${
                      !destacado 
                        ? 'bg-primary-500 text-white border-primary-500' 
                        : 'bg-transparent text-secondary-400 border-secondary-600 hover:border-secondary-500'
                    }`}
                  >
                    <X size={16} />
                    No
                  </Button>
                </div>
              </div>

              {/* Foto del Partido */}
              <div>
                <label className="block text-sm font-medium text-secondary-300 mb-2">
                  Foto del Partido
                </label>
                
                {/* Preview de la foto o botón para añadir */}
                {fotoPreview ? (
                  <div className="relative">
                    <img
                      src={fotoPreview}
                      alt="Foto del partido"
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      onClick={eliminarFoto}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={isLoading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <CameraCapture
                    onImageSelect={handleFotoSelect}
                    currentImage={foto}
                    buttonText="Añadir Foto del Partido"
                    disabled={isLoading}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Eliminar la sección de mensajes de estado de aquí (líneas 355-367) */}
        
        {isLoading && uploadProgress !== null && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-secondary-300">
              <span>{uploadProgress < 100 ? 'Subiendo imagen...' : 'Procesando información...'}</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-secondary-700 overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-secondary-700">
          <Button
            onClick={hasChanges ? handleGuardar : onClose}
            variant={hasChanges ? "primary" : "secondary"}
            className="flex-1"
            disabled={isLoading}
          >
            {isLoading ? 'Guardando...' : hasChanges ? 'Guardar' : 'Cerrar'}
          </Button>
          
          {/* Botón Ver Partido - Solo mostrar si hay URL del acta */}
          {partido?.urlActa && (
            <Button
              onClick={handleVerPartido}
              variant="outline"
              className="flex-1 flex items-center justify-center gap-2"
              disabled={isLoading}
            >
              <ExternalLink size={16} />
              Ver Partido
            </Button>
          )}
          
          <Button
            onClick={handleVerInformes}
            variant="outline"
            className="flex-1"
            disabled={isLoading}
          >
            Ver Informes
          </Button>
          
          <Button
            onClick={handleCrearInforme}
            variant="primary"
            className="flex-1"
            disabled={isLoading}
          >
            Crear Informe
          </Button>
        </div>
      </div>
    </Modal>
  );
};
